import type { TFunction } from "i18next";

export interface CoachmarkStep {
  title: string;
  body: string;
}

type AppToBuild = "electrical" | "electrical_us" | "plumbing" | "ibew_aptitude" | string;

/**
 * Builds the localized array of coachmark tour steps for the given app variant.
 *
 * Returns three steps for the `electrical_us` 3-step tour. For other apps, the
 * UI only renders the first two steps (Practice + AI), but we still return all
 * three so call sites can index by step number without having to special-case
 * shorter arrays.
 */
export function getCoachmarkSteps(
  t: TFunction,
  appToBuild: AppToBuild,
): CoachmarkStep[] {
  const practiceTitleKey =
    appToBuild === "ibew_aptitude"
      ? "coachmark.step_practice_title_ibew_aptitude"
      : "coachmark.step_practice_title";

  const practiceBodyKey =
    appToBuild === "electrical_us"
      ? "coachmark.step_practice_body_electrical_us"
      : appToBuild === "electrical"
        ? "coachmark.step_practice_body_electrical"
        : appToBuild === "plumbing"
          ? "coachmark.step_practice_body_plumbing"
          : appToBuild === "ibew_aptitude"
            ? "coachmark.step_practice_body_ibew_aptitude"
            : "coachmark.step_practice_body_default";

  const aiTitleKey =
    appToBuild === "plumbing"
      ? "coachmark.step_ai_title_plumbing"
      : appToBuild === "ibew_aptitude"
        ? "coachmark.step_ai_title_ibew_aptitude"
        : "coachmark.step_ai_title";

  const aiBodyKey =
    appToBuild === "electrical_us"
      ? "coachmark.step_ai_body_electrical_us"
      : appToBuild === "electrical"
        ? "coachmark.step_ai_body_electrical"
        : appToBuild === "plumbing"
          ? "coachmark.step_ai_body_plumbing"
          : appToBuild === "ibew_aptitude"
            ? "coachmark.step_ai_body_ibew_aptitude"
            : "coachmark.step_ai_body_electrical_us";

  const memorizeBodyKey =
    appToBuild === "electrical_us"
      ? "coachmark.step_memorize_body_electrical_us"
      : appToBuild === "electrical"
        ? "coachmark.step_memorize_body_electrical"
        : appToBuild === "plumbing"
          ? "coachmark.step_memorize_body_plumbing"
          : appToBuild === "ibew_aptitude"
            ? "coachmark.step_memorize_body_ibew_aptitude"
            : "coachmark.step_memorize_body_electrical_us";

  return [
    {
      title: t(practiceTitleKey) as string,
      body: t(practiceBodyKey) as string,
    },
    {
      title: t(aiTitleKey) as string,
      body: t(aiBodyKey) as string,
    },
    {
      title: t("coachmark.step_memorize_title") as string,
      body: t(memorizeBodyKey) as string,
    },
  ];
}
